import React, {FormEvent, useEffect, useState} from 'react';
import styles from './Login.module.scss';
import logo from '../../../assets/images/Color_CombatTv_Logo.svg'
import { normalizeUserInfo, useUserService } from "../../../services/user.service";
import {useUserState} from "../../../providers/user-state.provider.ts";
import router from "../../../providers/router";
import {Routes} from "../../../types/constants";
import { useCookies } from 'react-cookie';
import { useAuthModal } from "../../../providers/auth-modal.provider";
import { usePurchaseFlow } from '../../../hooks/usePurchaseFlow';
import { useSaveVideo } from '../../../hooks/useSaveVideo';
import { buildJoinUrl, getSafeInternalReturnTo } from '../../../utils/join';

interface LoginProps {}

const getErrorStatus = (error: unknown): number | undefined => (
    typeof (error as { status?: unknown })?.status === 'number'
        ? (error as { status: number }).status
        : undefined
);

const getStoredValue = (key: string): string | null => {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
};

const removeStoredValue = (key: string): void => {
    try {
        window.localStorage.removeItem(key);
    } catch {
        // Authentication and its navigation controls must work without browser storage.
    }
};

const Login = (props: LoginProps) => {
    const [, setCookie] = useCookies(['user'])
    const userState = useUserState();
    const userService = useUserService();
    const { isOpen, intent, closeModal } = useAuthModal();
    const { startPurchase } = usePurchaseFlow();
    const { saveVideo } = useSaveVideo();

    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [loginSuccess, setLoginSuccess] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [fieldErrors, setFieldErrors] = useState<{ username?: string; password?: string }>({});
    const [formError, setFormError] = useState<string | null>(null);
    const shouldNavigateToProductHome = () => (
        window.location.pathname === Routes.HOME || window.location.pathname === Routes.LOGIN
    );

    const handleBrowse = () => {
        removeStoredValue('redirectAfterLogin');
        closeModal();
        if (window.history.length > 1) {
            router.navigate(-1);
            return;
        }
        router.navigate(Routes.VIDEOS);
    };

    const submitForm = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsLoading(true); // Set loading state to true
        setFormError(null);
        setLoginSuccess(true);
        const formData = new FormData(event.target as HTMLFormElement);

        const data = Object.fromEntries(formData);
        const username = data.username?.toString().trim();
        // Passwords are opaque credentials; trimming would change a valid password.
        const password = data.password?.toString();

        const nextErrors = {
            username: username ? undefined : 'Username or email is required',
            password: password ? undefined : 'Password is required'
        };

        setFieldErrors(nextErrors);
        if (!username || !password) {
            setIsLoading(false);
            return;
        }

        let response: unknown;
        try {
            response = await userService.login({
                username,
                password
            }, { suppressAuthModalOnUnauthorized: true });
        } catch (error) {
            setLoginSuccess(false);
            setIsLoading(false); // Set loading state to false
            setFormError(getErrorStatus(error) === 401
                ? 'Invalid username or email, or password. Please try again.'
                : 'Sign in is temporarily unavailable. Please try again.');
            console.error(error);
            return;
        }

        const userInfo = normalizeUserInfo(response);
        if (!userInfo.userId || !userInfo.username || !userInfo.token) {
            setIsLoading(false);
            setFormError('Sign in could not be completed. Please try again.');
            console.error('Sign in response did not include a complete session.');
            return;
        }

        try {
            userState.setUserInfo(userInfo);
            setCookie('user', userInfo, { path: '/' })
            setLoginSuccess(true);
            setIsLoading(false); // Set loading state to false
            const storedRedirectUrl = getStoredValue('redirectAfterLogin');
            const redirectUrl = getSafeInternalReturnTo(storedRedirectUrl, Routes.VIDEOS);
            
            const pendingPurchaseId = getStoredValue('pendingVideoPurchaseId');
            if (pendingPurchaseId) {
                removeStoredValue('pendingVideoPurchaseId');
                removeStoredValue('redirectAfterLogin');
                closeModal();
                await startPurchase(pendingPurchaseId, userInfo.token);
                return;
            }

            const pendingSavedId = getStoredValue('pendingSavedVideoId');
            if (pendingSavedId) {
                removeStoredValue('pendingSavedVideoId');
                removeStoredValue('redirectAfterLogin');
                closeModal();
                await saveVideo(pendingSavedId, userInfo.token);
                return;
            }

            if (storedRedirectUrl) {
                removeStoredValue('redirectAfterLogin');
                closeModal();
                router.navigate(redirectUrl);
            } else {
                closeModal();
                if (shouldNavigateToProductHome()) {
                    router.navigate(Routes.VIDEOS);
                }
            }
        } catch (error) {
            setIsLoading(false); // Set loading state to false
            setFormError('You are signed in, but we could not complete the requested action. Please try again.');
            console.error(error);
        }
    }

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setShowPassword(false);
        setLoginSuccess(true);
        setIsLoading(false);
        setFormError(null);
        setFieldErrors({});
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    const titleCopy = intent === 'paid_content'
        ? 'Sign in to continue watching'
        : 'Welcome back to Combat TV';

    const subtitleCopy = intent === 'paid_content'
        ? 'You are almost there. Sign in to unlock premium access and return to your stream.'
        : 'Sign in when you are ready to jump back into premium fights and exclusive coverage.';

    return (
        <div className={styles['overlay']} role="dialog" aria-modal="true" aria-labelledby="login-title" aria-describedby="login-subtitle">
            <div className={styles['backdrop']} onClick={handleBrowse} aria-hidden="true" />
            <section className={styles['modal']}>
                <div className={styles['modalHeader']}>
                    <img src={logo} alt={'CombatTV'} className={styles['logo']}/>
                </div>
                <div className={styles['modalBody']}>
                    <div className={styles['intro']}>
                        <span className={styles['eyebrow']}>Member access</span>
                        <h1 className={styles['title']} id="login-title">{titleCopy}</h1>
                        <p className={styles['subtitle']} id="login-subtitle">
                            {subtitleCopy}
                        </p>
                    </div>
                    <div className={styles['formCard']}>
                        <p className={styles['formIntro']}>Sign in with your username or email</p>
                        <form className={styles['form']} onSubmit={(event) => {void submitForm(event)}}>
                            <div className={styles['fieldGroup']}>
                                <label className={styles['label']} htmlFor="username">Username or email</label>
                                <div className={styles['inputWrapper']}>
                                    <span className={"material-symbols-outlined " + styles['icon']}>person</span>
                                    <input
                                        key="username"
                                        id="username"
                                        name="username"
                                        type="text"
                                        className={styles['field']}
                                        placeholder={'Username or email'}
                                        onChange={() => {
                                            setFieldErrors((prev) => ({ ...prev, username: undefined }));
                                            setFormError(null);
                                        }}
                                        autoComplete="username"
                                    />
                                </div>
                                {fieldErrors.username ? <span className={styles['fieldError']}>{fieldErrors.username}</span> : null}
                            </div>
                            <div className={styles['fieldGroup']}>
                                <label className={styles['label']} htmlFor="password">Password</label>
                                <div className={styles['inputWrapper']}>
                                    <span className={"material-symbols-outlined " + styles['icon']}>lock</span>
                                    <input
                                        key="password"
                                        id="password"
                                        name="password"
                                        type={showPassword ? 'text' : 'password'}
                                        className={styles['field']}
                                        placeholder={'Password'}
                                        onChange={() => {
                                            setFieldErrors((prev) => ({ ...prev, password: undefined }));
                                            setFormError(null);
                                        }}
                                        autoComplete="current-password"
                                    />
                                    <button
                                        type="button"
                                        className={styles['iconButton']}
                                        onClick={() => setShowPassword(!showPassword)}
                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    >
                                        <span className="material-symbols-outlined">{ showPassword ? 'visibility' : 'visibility_off' }</span>
                                    </button>
                                </div>
                                {fieldErrors.password ? <span className={styles['fieldError']}>{fieldErrors.password}</span> : null}
                            </div>

                            {!loginSuccess || formError ? (
                                <div className={styles['formError']} role="alert">
                                    <span>{formError || 'Username or email and/or password incorrect, try again'}</span>
                                </div>
                            ) : null}

                            <div className={styles['formActions']}>
                                <button type="submit" className={styles['loginButton']} disabled={isLoading}>
                                    {isLoading ? (
                                        <div className="flex justify-center items-center">
                                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        </div>
                                    ) : 'Sign In'}
                                </button>
                            </div>
                        </form>
                    </div>
                    <div className={styles['copy']}>
                        <ul className={styles['benefits']}>
                            <li>Watch full fights and exclusive replays</li>
                            <li>Pick up on any device, any time</li>
                            <li>Manage your membership in seconds</li>
                        </ul>
                        <div className={styles['subscribeCard']}>
                            <div>
                                <h2>Not a member yet?</h2>
                                <p>Become a member when you are ready to stream every premium event.</p>
                            </div>
                            <button
                                type="button"
                                className={styles['secondaryButton']}
                                onClick={() => {
                                    const returnTo = getStoredValue('redirectAfterLogin');
                                    closeModal();
                                    router.navigate(buildJoinUrl({
                                        source: 'login',
                                        returnTo
                                    }));
                                }}
                            >
                                Create account
                            </button>
                        </div>
                    </div>
                </div>
                <div className={styles['modalFooter']}>
                    <div className={styles['footerLinks']}>
                        <a href={'/forgot_password'}>Forgot Password</a>
                        <a href={Routes.CONTACT}>Need help?</a>
                    </div>
                    <button type="button" className={styles['browseButton']} onClick={handleBrowse}>
                        Continue browsing
                        <span className="material-symbols-outlined">arrow_right_alt</span>
                    </button>
                </div>
            </section>
        </div>
    );
}

export default Login;
